import { IconsId } from '@types';

export type StatusTone = 'success' | 'warning' | 'error' | 'neutral';

interface StatusConfigEntry {
  label: string;
  iconName: IconsId;
  tone: StatusTone;
}

const normalizeStatusKey = (raw: string) => raw.toLowerCase().replace(/[_\- ]+/g, '');

// Run status (CONTEXT.md): execution state of an async job (dataset pipeline, assessment).
// Progresses forward, converges to a terminal state. Never mixed with Lifecycle status.
const RUN_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  notstarted: { label: 'Not started', iconName: 'dash', tone: 'neutral' },
  pending: { label: 'Pending', iconName: 'clock', tone: 'neutral' },
  running: { label: 'Running', iconName: 'arrows-rotate', tone: 'warning' },
  retry: { label: 'Retry', iconName: 'arrows-rotate', tone: 'warning' },
  finished: { label: 'Finished', iconName: 'check', tone: 'success' },
  completed: { label: 'Completed', iconName: 'check', tone: 'success' },
  success: { label: 'Success', iconName: 'check', tone: 'success' },
  successwithwarnings: { label: 'Success with warnings', iconName: 'exclamation-triangle', tone: 'warning' },
  successwitherrors: { label: 'Success with errors', iconName: 'exclamation-triangle', tone: 'success' },
  failed: { label: 'Failed', iconName: 'exclamation-triangle', tone: 'error' },
  cancelled: { label: 'Cancelled', iconName: 'dash', tone: 'neutral' },
};

// Lifecycle status (CONTEXT.md): where a long-lived entity sits in its lifecycle. Reversible.
const LIFECYCLE_STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  live: { label: 'Live', iconName: 'check', tone: 'success' },
  inreview: { label: 'In review', iconName: 'clock', tone: 'warning' },
  notlive: { label: 'Not live', iconName: 'dash', tone: 'neutral' },
};

const STATUS_CONFIG: Record<string, StatusConfigEntry> = {
  ...RUN_STATUS_CONFIG,
  ...LIFECYCLE_STATUS_CONFIG,
};

const prettifyStatus = (raw: string) => {
  const words = raw.toLowerCase().replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const getStatusConfig = (status: string): StatusConfigEntry => {
  const config = STATUS_CONFIG[normalizeStatusKey(status)];
  if (config) return config;
  return { label: prettifyStatus(status), iconName: 'dash', tone: 'neutral' };
};
